'use client'
import * as amplitude from '@amplitude/analytics-browser'
import Script from 'next/script'
import { formatEventName } from '@/lib/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/lib/analytics/analytics.constants'
import { INTERCOM_WORKSPACE_ID } from '@/lib/constants'
import { isDevelopment } from '@/lib/general-helpers'

declare global {
	interface Window {
		intercomSettings: {
			app_id: string
		}
		// biome-ignore lint/suspicious/noExplicitAny: Intercom's API requires flexible arguments
		Intercom: (...args: any[]) => void
		intercomOpenSource?: 'cta_link' | 'launcher'
	}
}

export const IntercomScript = () => {
	const workspaceId = INTERCOM_WORKSPACE_ID

	if (!workspaceId || isDevelopment()) {
		return null
	}

	return (
		// biome-ignore lint/correctness/useUniqueElementIds: static ID for third-party script container
		<Script
			id="intercom-widget"
			strategy="lazyOnload"
			src={`https://widget.intercom.io/widget/${workspaceId}`}
			onLoad={() => {
				window.intercomSettings = { app_id: workspaceId }

				const w = window
				const ic = w.Intercom
				if (typeof ic === 'function') {
					ic('reattach_activator')
					ic('update', w.intercomSettings)
				} else {
					const i = (...args: unknown[]) => {
						i.c(args)
					}
					// biome-ignore lint/suspicious/noExplicitAny: Intercom queue requires flexible structure
					i.q = [] as any[]
					// biome-ignore lint/suspicious/noExplicitAny: Intercom callback requires flexible arguments
					i.c = (args: any) => {
						i.q.push(args)
					}
					w.Intercom = i
				}

				w.Intercom('onShow', () => {
					amplitude.track(formatEventName(ANALYTICS_EVENTS.INTERCOM_MESSENGER_OPENED), {
						source: w.intercomOpenSource ?? 'launcher',
						page_path: w.location.pathname,
					})
					w.intercomOpenSource = undefined
				})

				w.Intercom('onUserEmailSupplied', () => {
					amplitude.track(formatEventName(ANALYTICS_EVENTS.INTERCOM_EMAIL_SUPPLIED), {
						page_path: w.location.pathname,
					})
				})
			}}
		/>
	)
}
